import React, { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, Alert } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PageHeader } from '@/components/residents/redesign';
import { MobileNotificationCard } from '@/components/residents/notification/mobile/MobileNotificationCard';
import { NotificationEmptyState } from '@/components/residents/notification/mobile/NotificationEmptyState';
import { NotificationsScreenSkeleton } from './NotificationsScreenSkeleton';
import { LeaveFeedbackModal } from '@/components/residents/marketplace';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';
import { useMarketplaceFeedbackModal } from '@/hooks/common/useMarketplaceFeedbackModal';
import { useAppSelector, useAppDispatch } from '@/store';
import { fetchHomeData } from '@/store/slices/residents/homeSlice';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { logger } from '@/utils/common/logger';

const HP = REDESIGN_SPACING.horizontalPadding;

export const ResidentNotificationsScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const { user } = useAppSelector((state) => state.auth);
  const [refreshing, setRefreshing] = useState(false);

  const propertyName = user?.property?.marketingName || user?.property?.legalName || '';

  const {
    notifications,
    isLoading,
    reload,
    isUnread,
    markAsRead,
    deleteNotification,
    isMarketplaceFeedback,
    markPropertyChatAsViewed,
  } = useNotifications();
  
  const feedbackModal = useMarketplaceFeedbackModal({ onSuccess: reload });

  useEffect(() => {
    if (user?.propertyId) {
      dispatch(fetchHomeData(user.propertyId));
    }
  }, [dispatch, user?.propertyId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } catch (error) {
      console.error('Failed to refresh notifications:', error);
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  const handleNotificationExpand = useCallback(async (notification: NotificationItem) => {
    try {
      await markAsRead(notification);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [markAsRead]);

  const handleDeleteNotification = useCallback((notification: NotificationItem) => {
    if (isMarketplaceFeedback(notification)) return;
    
    Alert.alert(
      strings.notifications.mobile.delete.title,
      strings.notifications.mobile.delete.message,
      [
        {
          text: strings.notifications.mobile.delete.cancel,
          style: 'cancel',
        },
        {
          text: strings.notifications.mobile.delete.confirm,
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteNotification(notification.id);
            } catch (error) {
              console.error('Failed to delete notification:', error);
              Alert.alert(strings.alert.error, 'Failed to delete notification. Please try again.');
            }
          },
        },
      ],
      { cancelable: true }
    );
  }, [deleteNotification, isMarketplaceFeedback]);

  const handleMarketplaceFeedbackPress = useCallback((notification: NotificationItem) => {
    feedbackModal.openModal(notification);
  }, [feedbackModal]);

  const handleCorkBoardWarningPress = useCallback(async (notification: NotificationItem) => {
    try {
      await markAsRead(notification);
    } catch (e) {
      logger.warn('notificationMarkReadFailed', e);
    }

    const threadId = notification.corkBoardNotification?.metadata?.threadId;
    if (threadId) {
      navigation.navigate('ThreadDetail', { threadId: String(threadId) });
    }
  }, [markAsRead, navigation]);

  const handlePropertyChatPress = useCallback(() => {
    markPropertyChatAsViewed();
    navigation.navigate('ResidentMessages');
  }, [markPropertyChatAsViewed, navigation]);

  const renderItem = useCallback(({ item }: { item: NotificationItem }) => (
    <MobileNotificationCard
      notification={item}
      isUnread={isUnread(item)}
      onExpand={handleNotificationExpand}
      onDelete={handleDeleteNotification}
      onMarketplaceFeedbackPress={handleMarketplaceFeedbackPress}
      onCorkBoardWarningPress={handleCorkBoardWarningPress}
      onPropertyChatPress={handlePropertyChatPress}
    />
  ), [isUnread, handleNotificationExpand, handleDeleteNotification, handleMarketplaceFeedbackPress, handleCorkBoardWarningPress, handlePropertyChatPress]);

  const keyExtractor = useCallback((item: NotificationItem) => `notification-${item.id}`, []);

  const renderEmptyState = useCallback(() => (
    <NotificationEmptyState />
  ), []);

  const showLoading = isLoading && (!notifications || notifications.length === 0);

  return (
    <>
      {showLoading ? (
        <NotificationsScreenSkeleton />
      ) : (
        <FlatList
          data={notifications || []}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          ListHeaderComponent={
            <PageHeader
              title={strings.notifications.mobile.screenTitle}
              subtitle={propertyName}
            />
          }
          contentContainerStyle={[
            styles.listContent,
            { paddingBottom: 40 + insets.bottom },
            (!notifications || notifications.length === 0) && styles.listContentEmpty,
          ]}
          ListEmptyComponent={renderEmptyState}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={colors.text.primary}
            />
          }
          showsVerticalScrollIndicator={false}
        />
      )}
      <LeaveFeedbackModal
        visible={feedbackModal.isVisible}
        feedbackRequest={feedbackModal.feedbackRequest}
        onClose={feedbackModal.closeModal}
        onSubmit={feedbackModal.submitFeedback}
        isSubmitting={feedbackModal.isSubmitting}
      />
    </>
  );
};

const styles = StyleSheet.create({
  listContent: {
    paddingHorizontal: HP,
    gap: 16,
    paddingBottom: 40,
  },
  listContentEmpty: {
    flexGrow: 1,
  },
});

export default ResidentNotificationsScreen;
